import { CircularProgress } from '@material-ui/core';
import { Button, Card, CardContent, Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import PublicLayout from 'v2/components/Shared/PublicLayout';
import BankAccountService from 'v2/services/bankAccount';

const ConfirmBankAccount = () => {
  const { uuid } = useParams<{ uuid?: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [account, setAccount] = useState(null);
  const [hasSent, setHasSent] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [expiredToken, setExpiredToken] = useState<boolean>(false);
  const [successConfirmation, setSuccessConfirmation] = useState<boolean>(false);

  const qs = new URLSearchParams(useLocation().search);
  const token = qs.get('token');

  useEffect(() => {
    if (!uuid || !token) return null;
    BankAccountService.getBankAccountByUuid(uuid, token)
      .then((account) => {
        setAccount(account);
        setIsLoading(false);
      })
      .catch(() => {
        setHasSent(false);
        setIsLoading(false);
        setExpiredToken(true);
      });
  }, [token, uuid]);

  const confirmBankAccount = useCallback(() => {
    setIsSending(true);
    BankAccountService.confirmBankAccount(uuid, token)
      .then(() => {
        setHasSent(true);
        setIsSending(false);
        setSuccessConfirmation(true);
      })
      .catch(() => {
        setHasSent(false);
        setIsSending(false);
        setExpiredToken(true);
      });
  }, [token, uuid, setIsSending]);

  if (isLoading) return <GlobalLoading />;
  if (expiredToken) return <TokenExpired />;
  if (successConfirmation) return <SuccessConfirmation />;

  return (
    <PublicLayout>
      <Grid container direction='row' justify='center' alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='subtitle1' style={{ marginTop: '22px', textAlign: 'center' }}>
                Por favor, confirme seus dados bancários:
              </Typography>
            </CardContent>

            <CardContent style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'left' }}>
                <p>
                  <b>Banco:</b> {account?.bank_id}
                </p>
                <p>
                  <b>Agência:</b> {account?.agency} - {account?.agency_digit}
                </p>
                <p>
                  <b>Conta: </b> {account?.account} - {account?.account_digit}
                </p>
                <p>
                  <b>Moeda:</b> {account?.currency?.code}
                </p>
              </div>
            </CardContent>

            <CardContent style={{ textAlign: 'center' }}>
              {hasSent ? (
                <Typography>Dados bancários confirmados!</Typography>
              ) : (
                <Button disabled={isSending} color='secondary' variant='contained' onClick={confirmBankAccount}>
                  {isSending && <CircularProgress size={16} />} <span style={{ marginLeft: 10 }}>Confirmar Dados Bancários</span>
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PublicLayout>
  );
};

const TokenExpired = () => {
  return (
    <PublicLayout>
      <Grid container direction='row' justify='center' alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='subtitle1' style={{ marginTop: '32px', textAlign: 'center' }}>
                <p>Esse link está expirado ou inválido!</p>
                <p>Para solicitar um novo link de confirmação de dados bancários, acesse suas contas bancárias pelo botão abaixo:</p>
              </Typography>
            </CardContent>

            <CardContent style={{ textAlign: 'center' }}>
              <a href={'/producer/profile/bankdata'}>
                <Button color='secondary' variant='contained'>
                  Acessar Minhas Contas Bancárias
                </Button>
              </a>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PublicLayout>
  );
};

const SuccessConfirmation = () => {
  return (
    <PublicLayout>
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='subtitle1' style={{ marginTop: '32px', textAlign: 'center' }}>
                <p>Dados bancários confirmados com sucesso!</p>
                <p>
                  Agora é com a gente, nosso time financeiro fará a verificação dos dados. Esse processo costuma ser rápido (de 1 a 5 dias úteis).
                  Fique tranquilo, vamos nos esforçar para ser no menor tempo possível.
                </p>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PublicLayout>
  );
};

export default ConfirmBankAccount;
